import React from 'react';
import './ParticipantGroupList.css';
import ParticipantGroupDot from '../ParticipantGroupDot';
import CustomTogglebox from '../../input/CustomTogglebox';
import GroupStatusBadge from './GroupStatusBadge';
import { memberCountLabel } from './groupAdminUtils';
import { SIGNUP_CHANNEL_LABEL } from '../../../util/participantGroups';

function GroupRow({ group, selected, onSelect, order }) {
  // Guest has no memberships: its plural ("Gäste") stands in for the member count.
  const meta = [
    group.isGuest ? group.pluralName : memberCountLabel(group),
    SIGNUP_CHANNEL_LABEL[group.signupChannel],
  ].filter(Boolean).join(' · ');

  return (
    <li className={`groupListRow${selected ? ' isSelected' : ''}${group.isArchived ? ' isArchived' : ''}`}>
      <button
        type="button"
        className="groupListRowMain"
        onClick={() => onSelect(group.id)}
        aria-current={selected ? 'true' : undefined}
      >
        <span className="groupListRowDot">
          <ParticipantGroupDot participantGroup={group} size="md" withTooltip={false} />
        </span>
        <span className="groupListRowText">
          <span className="groupListRowNameLine">
            <span className="groupListRowName">{group.name}</span>
            {group.isGuest && <GroupStatusBadge kind="system" />}
            {group.isArchived && <GroupStatusBadge kind="archived" />}
          </span>
          <span className="groupListRowMeta">{meta}</span>
        </span>
      </button>
      {order && (
        <span className="groupListRowOrder">
          <button
            type="button"
            className="groupListOrderBtn"
            onClick={() => order.onMove(group.id, -1)}
            disabled={order.disabled || order.isFirst}
            aria-label={`${group.name} nach oben verschieben`}
            title={order.disabledReason || 'Nach oben'}
          >
            <span className="material-symbols-rounded" aria-hidden="true">keyboard_arrow_up</span>
          </button>
          <button
            type="button"
            className="groupListOrderBtn"
            onClick={() => order.onMove(group.id, 1)}
            disabled={order.disabled || order.isLast}
            aria-label={`${group.name} nach unten verschieben`}
            title={order.disabledReason || 'Nach unten'}
          >
            <span className="material-symbols-rounded" aria-hidden="true">keyboard_arrow_down</span>
          </button>
        </span>
      )}
    </li>
  );
}

/**
 * Group list of /groups: the guest group pinned on top as fixed system group, below the other groups in
 * their display order (SortOrder) with up/down buttons, optionally incl. archived groups.
 * `children`: further sections below the groups (the roles).
 */
function ParticipantGroupList({
  guest, groups, selectedId, onSelect, onMove, reorderDisabled, reorderDisabledReason,
  archivedCount, showArchived, onToggleArchived, children,
}) {
  return (
    <div className="groupList">
      {guest && (
        <section className="groupListSection" aria-label="Systemgruppe">
          <h3 className="groupListSectionTitle">Systemgruppe</h3>
          <ul className="groupListRows">
            <GroupRow group={guest} selected={selectedId === guest.id} onSelect={onSelect} />
          </ul>
          <p className="groupListHint">Alle Personen ohne Mitgliedschaft – nicht archivier- oder löschbar.</p>
        </section>
      )}

      <section className="groupListSection" aria-label="Gruppen">
        <h3 className="groupListSectionTitle">Gruppen</h3>
        {groups.length === 0 ? (
          <p className="groupListEmpty">Noch keine weitere Gruppe – lege eine über „Neue Gruppe“ an.</p>
        ) : (
          <ul className="groupListRows">
            {groups.map((g, index) => (
              <GroupRow
                key={g.id}
                group={g}
                selected={selectedId === g.id}
                onSelect={onSelect}
                order={{
                  onMove,
                  isFirst: index === 0,
                  isLast: index === groups.length - 1,
                  disabled: reorderDisabled,
                  disabledReason: reorderDisabledReason,
                }}
              />
            ))}
          </ul>
        )}
        {groups.length > 1 && (
          <p className="groupListHint">Die Reihenfolge gilt für alle Listen und Auswahlen im Tool; Gast steht dort immer zuletzt.</p>
        )}
        {archivedCount > 0 && (
          <CustomTogglebox
            placeholder={`Archivierte anzeigen (${archivedCount})`}
            checked={showArchived}
            onChange={e => onToggleArchived(e.target.checked)}
          />
        )}
      </section>

      {children}
    </div>
  );
}

export default ParticipantGroupList;
